import React, { useState, useMemo } from 'react';
import ToolLayout from '../components/ToolLayout';

interface PathResult {
  path: string;
  value: any;
  type: string;
  success: boolean;
  error?: string;
}

const JsonPathTester: React.FC = () => {
  const [jsonInput, setJsonInput] = useState('');
  const [pathInput, setPathInput] = useState('');
  const [parsedJson, setParsedJson] = useState<any>(null);
  const [jsonError, setJsonError] = useState('');

  // Parse JSON input
  const parseJson = useMemo(() => {
    if (!jsonInput.trim()) {
      setParsedJson(null);
      setJsonError('');
      return null;
    }

    try {
      const parsed = JSON.parse(jsonInput);
      setParsedJson(parsed);
      setJsonError('');
      return parsed;
    } catch (error) {
      setJsonError(`JSON không hợp lệ: ${error instanceof Error ? error.message : 'Unknown error'}`);
      setParsedJson(null);
      return null;
    }
  }, [jsonInput]);

  // Simple JSONPath implementation
  const evaluateJsonPath = (obj: any, path: string): PathResult => {
    if (!obj || !path) {
      return {
        path,
        value: undefined,
        type: 'undefined',
        success: false,
        error: 'JSON hoặc path không hợp lệ'
      };
    }

    try {
      // Remove leading $ if present
      const cleanPath = path.startsWith('$') ? path.slice(1) : path;
      
      // Split path by dots and brackets
      const parts = cleanPath.split(/[\.\[\]]/).filter(part => part !== '');
      
      let current = obj;
      let currentPath = '$';

      for (let i = 0; i < parts.length; i++) {
        const part = parts[i];
        
        if (part === '') {
          continue;
        }

        // Handle array indices
        if (/^\d+$/.test(part)) {
          const index = parseInt(part, 10);
          if (Array.isArray(current) && index >= 0 && index < current.length) {
            current = current[index];
            currentPath += `[${index}]`;
          } else {
            return {
              path: currentPath,
              value: undefined,
              type: 'undefined',
              success: false,
              error: `Index ${index} không tồn tại trong array`
            };
          }
        } else {
          // Handle object properties
          if (current && typeof current === 'object' && part in current) {
            current = current[part];
            currentPath += currentPath === '$' ? part : `.${part}`;
          } else {
            return {
              path: currentPath,
              value: undefined,
              type: 'undefined',
              success: false,
              error: `Property '${part}' không tồn tại`
            };
          }
        }
      }

      return {
        path: currentPath,
        value: current,
        type: typeof current,
        success: true
      };
    } catch (error) {
      return {
        path,
        value: undefined,
        type: 'undefined',
        success: false,
        error: `Lỗi đánh giá path: ${error instanceof Error ? error.message : 'Unknown error'}`
      };
    }
  };

  const pathResult = useMemo(() => {
    if (!parsedJson || !pathInput.trim()) {
      return null;
    }
    return evaluateJsonPath(parsedJson, pathInput);
  }, [parsedJson, pathInput]);

  const loadSampleData = () => {
    const sampleJson = {
      "user": {
        "id": 123,
        "name": "Nguyễn Văn A",
        "email": "nva@example.com",
        "profile": {
          "age": 30,
          "location": "Hà Nội",
          "preferences": ["music", "books", "travel"]
        }
      },
      "orders": [
        {
          "id": 1001,
          "total": 150000,
          "items": ["Laptop", "Mouse"]
        },
        {
          "id": 1002,
          "total": 75000,
          "items": ["Book", "Pen"]
        }
      ],
      "metadata": {
        "version": "1.0",
        "timestamp": "2024-01-01T00:00:00Z"
      }
    };

    setJsonInput(JSON.stringify(sampleJson, null, 2));
    setPathInput('$.user.name');
  };

  const clearInputs = () => {
    setJsonInput('');
    setPathInput('');
  };

  const commonPaths = [
    '$.user.name',
    '$.user.profile.age',
    '$.user.profile.preferences[0]',
    '$.orders[0].total',
    '$.orders[1].items',
    '$.metadata.version'
  ];

  const formatValue = (value: any): string => {
    if (value === null) {
      return 'null';
    }
    if (value === undefined) {
      return 'undefined';
    }
    if (typeof value === 'string') {
      return `"${value}"`;
    }
    if (typeof value === 'object') {
      return JSON.stringify(value, null, 2);
    }
    return String(value);
  };

  const getValueType = (value: any): string => {
    if (value === null) {
      return 'null';
    }
    if (Array.isArray(value)) {
      return 'array';
    }
    return typeof value;
  };

  return (
    <ToolLayout
      title="JSON Path Tester"
      description="Kiểm tra và đánh giá đường dẫn JSON với JSONPath syntax"
    >
      <div className="json-path-tester">
        {/* Controls */}
        <div className="controls-section">
          <div className="action-buttons">
            <button className="btn btn-primary" onClick={loadSampleData}>
              Tải dữ liệu mẫu
            </button>
            <button className="btn btn-secondary" onClick={clearInputs}>
              Xóa tất cả
            </button>
          </div>
        </div>

        {/* Input sections */}
        <div className="inputs-grid">
          {/* JSON Input */}
          <div className="input-section">
            <label className="input-label">JSON Data:</label>
            <textarea
              className={`json-textarea ${jsonError ? 'error' : ''}`}
              value={jsonInput}
              onChange={(e) => setJsonInput(e.target.value)}
              placeholder='{"key": "value", "array": [1, 2, 3]}'
              rows={15}
            />
            {jsonError && (
              <div className="error-message">{jsonError}</div>
            )}
            {parsedJson && !jsonError && (
              <div className="success-message">✅ JSON hợp lệ</div>
            )}
          </div>

          {/* Path Input */}
          <div className="input-section">
            <label className="input-label">JSONPath:</label>
            <input
              type="text"
              className="path-input"
              value={pathInput}
              onChange={(e) => setPathInput(e.target.value)}
              placeholder="$.user.name"
            />
            
            {/* Common paths */}
            <div className="common-paths">
              <label className="common-paths-label">Đường dẫn phổ biến:</label>
              <div className="common-paths-grid">
                {commonPaths.map((path) => (
                  <button
                    key={path}
                    className="common-path-btn"
                    onClick={() => setPathInput(path)}
                  >
                    {path}
                  </button>
                ))}
              </div>
            </div>

            {/* Result */}
            {pathResult && (
              <div className="result-section">
                <h4>Kết quả:</h4>
                {pathResult.success ? (
                  <div className="result-success">
                    <div className="result-meta">
                      <span className="result-path">Path: {pathResult.path}</span>
                      <span className="result-type">Type: {getValueType(pathResult.value)}</span>
                    </div>
                    <div className="result-value">
                      <pre>{formatValue(pathResult.value)}</pre>
                    </div>
                  </div>
                ) : (
                  <div className="result-error">
                    <div className="error-icon">❌</div>
                    <div className="error-text">{pathResult.error}</div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Documentation */}
        <div className="docs-section">
          <h3>📖 Hướng dẫn JSONPath</h3>
          <div className="docs-grid">
            <div className="doc-item">
              <strong>$</strong>
              <span>Root element</span>
              <code>$</code>
            </div>
            <div className="doc-item">
              <strong>.property</strong>
              <span>Object property</span>
              <code>$.user.name</code>
            </div>
            <div className="doc-item">
              <strong>[index]</strong>
              <span>Array index</span>
              <code>$.items[0]</code>
            </div>
            <div className="doc-item">
              <strong>.property[index]</strong>
              <span>Nested access</span>
              <code>$.user.orders[1].total</code>
            </div>
          </div>
        </div>

        {/* Tips */}
        <div className="tips-section">
          <h4>💡 Mẹo sử dụng:</h4>
          <ul className="tips-list">
            <li>Sử dụng <code>$</code> để bắt đầu từ root element</li>
            <li>Dùng dấu chấm <code>.</code> để truy cập properties của object</li>
            <li>Dùng <code>[index]</code> để truy cập elements trong array</li>
            <li>Kết hợp cả hai để truy cập nested data: <code>$.user.orders[0].id</code></li>
            <li>Tool hỗ trợ JSONPath cơ bản, phù hợp cho hầu hết use cases</li>
          </ul>
        </div>
      </div>

      <style>{`
        .json-path-tester {
          max-width: 1200px;
          margin: 0 auto;
        }

        .controls-section {
          background: var(--card-background);
          padding: 1rem;
          border-radius: 8px;
          margin-bottom: 1.5rem;
          border: 1px solid var(--border-color);
        }

        .action-buttons {
          display: flex;
          gap: 1rem;
          flex-wrap: wrap;
        }

        .inputs-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 1.5rem;
          margin-bottom: 1.5rem;
        }

        @media (max-width: 768px) {
          .inputs-grid {
            grid-template-columns: 1fr;
          }
        }

        .input-section {
          background: var(--card-background);
          padding: 1.5rem;
          border-radius: 8px;
          border: 1px solid var(--border-color);
        }

        .input-label {
          display: block;
          font-weight: 600;
          margin-bottom: 0.5rem;
          color: var(--text-primary);
        }

        .json-textarea {
          width: 100%;
          padding: 0.75rem;
          border: 1px solid var(--border-color);
          border-radius: 6px;
          background: var(--input-background);
          color: var(--text-primary);
          font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
          font-size: 0.9rem;
          line-height: 1.5;
          resize: vertical;
          min-height: 300px;
        }

        .json-textarea.error {
          border-color: #e53e3e;
        }

        .json-textarea:focus {
          outline: none;
          border-color: var(--primary-color);
          box-shadow: 0 0 0 3px rgba(66, 153, 225, 0.1);
        }

        .path-input {
          width: 100%;
          padding: 0.75rem;
          border: 1px solid var(--border-color);
          border-radius: 6px;
          background: var(--input-background);
          color: var(--text-primary);
          font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
          font-size: 0.9rem;
          margin-bottom: 1rem;
        }

        .path-input:focus {
          outline: none;
          border-color: var(--primary-color);
          box-shadow: 0 0 0 3px rgba(66, 153, 225, 0.1);
        }

        .error-message {
          margin-top: 0.5rem;
          padding: 0.5rem;
          background: #fed7d7;
          color: #c53030;
          border-radius: 4px;
          font-size: 0.9rem;
        }

        .success-message {
          margin-top: 0.5rem;
          padding: 0.5rem;
          background: #c6f6d5;
          color: #22543d;
          border-radius: 4px;
          font-size: 0.9rem;
        }

        .common-paths {
          margin-top: 1rem;
        }

        .common-paths-label {
          display: block;
          font-weight: 600;
          margin-bottom: 0.5rem;
          color: var(--text-primary);
          font-size: 0.9rem;
        }

        .common-paths-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
          gap: 0.5rem;
        }

        .common-path-btn {
          padding: 0.5rem;
          border: 1px solid var(--border-color);
          border-radius: 4px;
          background: var(--input-background);
          color: var(--text-primary);
          font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
          font-size: 0.8rem;
          cursor: pointer;
          transition: all 0.2s ease;
        }

        .common-path-btn:hover {
          background: var(--primary-color);
          color: white;
          border-color: var(--primary-color);
        }

        .result-section {
          margin-top: 1.5rem;
          padding: 1rem;
          background: var(--input-background);
          border-radius: 6px;
          border: 1px solid var(--border-color);
        }

        .result-section h4 {
          margin: 0 0 1rem 0;
          color: var(--text-primary);
        }

        .result-success {
          color: var(--text-primary);
        }

        .result-meta {
          display: flex;
          gap: 1rem;
          margin-bottom: 0.75rem;
          flex-wrap: wrap;
        }

        .result-path, .result-type {
          padding: 0.25rem 0.5rem;
          background: var(--card-background);
          border-radius: 4px;
          font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
          font-size: 0.8rem;
          border: 1px solid var(--border-color);
        }

        .result-value {
          background: var(--card-background);
          border: 1px solid var(--border-color);
          border-radius: 4px;
          padding: 0.75rem;
        }

        .result-value pre {
          margin: 0;
          font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
          font-size: 0.9rem;
          line-height: 1.4;
          color: var(--text-primary);
          white-space: pre-wrap;
          word-break: break-word;
        }

        .result-error {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          color: #c53030;
        }

        .error-icon {
          font-size: 1.2rem;
        }

        .error-text {
          font-size: 0.9rem;
        }

        .docs-section {
          background: var(--card-background);
          padding: 1.5rem;
          border-radius: 8px;
          margin-bottom: 1.5rem;
          border: 1px solid var(--border-color);
        }

        .docs-section h3 {
          margin: 0 0 1rem 0;
          color: var(--text-primary);
        }

        .docs-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
          gap: 1rem;
        }

        .doc-item {
          display: flex;
          flex-direction: column;
          gap: 0.25rem;
          padding: 0.75rem;
          background: var(--input-background);
          border-radius: 6px;
          border: 1px solid var(--border-color);
        }

        .doc-item strong {
          color: var(--text-primary);
          font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
        }

        .doc-item span {
          color: var(--text-secondary);
          font-size: 0.9rem;
        }

        .doc-item code {
          background: var(--card-background);
          padding: 0.25rem 0.5rem;
          border-radius: 3px;
          font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
          font-size: 0.8rem;
          color: var(--text-primary);
          border: 1px solid var(--border-color);
        }

        .btn {
          padding: 0.5rem 1rem;
          border: 1px solid var(--border-color);
          border-radius: 6px;
          background: var(--input-background);
          color: var(--text-primary);
          cursor: pointer;
          transition: all 0.2s ease;
          font-size: 0.9rem;
        }

        .btn-primary {
          background: var(--primary-color);
          color: white;
          border-color: var(--primary-color);
        }

        .btn-secondary {
          background: var(--input-background);
          color: var(--text-primary);
        }

        .btn:hover {
          transform: translateY(-1px);
          box-shadow: 0 2px 4px rgba(0,0,0,0.1);
        }

        .tips-section {
          background: var(--card-background);
          padding: 1.5rem;
          border-radius: 8px;
          border: 1px solid var(--border-color);
        }

        .tips-section h4 {
          margin: 0 0 1rem 0;
          color: var(--text-primary);
        }

        .tips-list {
          margin: 0;
          padding-left: 1.5rem;
          color: var(--text-secondary);
        }

        .tips-list li {
          margin-bottom: 0.5rem;
          line-height: 1.5;
        }

        .tips-list code {
          background: var(--input-background);
          padding: 0.2rem 0.4rem;
          border-radius: 3px;
          font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
          font-size: 0.85rem;
          color: var(--text-primary);
          border: 1px solid var(--border-color);
        }
      `}</style>
    </ToolLayout>
  );
};

export default JsonPathTester;